import React, { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useAuth } from '../contexts/AuthContext';
import { createClient } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price_month: number;
  price_year: number;
  stripe_price_id_month?: string;
  stripe_price_id_year?: string;
}

const AccountPage = () => {
  const { user } = useAuth();
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStripeCustomerId = async () => {
      if (!user?.id) return;
      const { data, error } = await supabase
        .from('user_credits')
        .select('stripe_customer_id')
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching stripe customer id:', error);
        setStripeCustomerId(null);
      } else {
        setStripeCustomerId(data?.stripe_customer_id);
      }
      setLoading(false);
    };
    fetchStripeCustomerId();
  }, [user]);

  useEffect(() => {
    // Fetch all active credit packs for display
    const fetchPacks = async () => {
      const { data, error } = await supabase
        .from('credit_packs')
        .select('*')
        .order('credits');
      if (error) {
        console.error('Error fetching credit packs:', error);
        setPacks([]);
      } else {
        setPacks(data || []);
      }
    };
    fetchPacks();
  }, []);

  useEffect(() => {
    // If not loading and no subscription, redirect to buy credits
    if (!loading && !stripeCustomerId) {
      navigate('/buy-credits');
    }
  }, [loading, stripeCustomerId, navigate]);

  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      // Získej JWT token uživatele ze Supabase Auth
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error('Chybí přihlášení uživatele (JWT token)');
      const response = await fetch('https://fvotdlmvtjubsqhsmywe.supabase.co/functions/v1/billing-portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          userId: user?.id,
          return_url: 'https://www.openstudio.cz/account',
        }),
      });
      const data = await response.json();
      if (data.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error(data.error?.message || 'Portal URL not received');
      }
    } catch (error) {
      alert(`Nepodařilo se otevřít správu předplatného: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container py-8">
          <div className="text-center">Loading...</div>
        </main>
      </div>
    );
  }

  // If no subscription, don't render anything (will redirect)
  if (!stripeCustomerId) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Můj účet</h1>
        </div>
        <div className="bg-white rounded-lg shadow p-8 max-w-xl mx-auto">
          <div className="mb-6">
            <p className="text-lg font-medium mb-2">Změnit tarif nebo spravovat předplatné a platební údaje:</p>
            <button
              onClick={handleManageSubscription}
              className="bg-[#14A48D] hover:bg-[#11997C] text-white font-bold py-3 px-6 rounded transition-colors text-lg"
            >
              Změnit tarif / Spravovat předplatné
            </button>
          </div>
        </div>
        {/* Subscription packs display - now outside the white box and full width */}
        <div className="container mx-auto mt-12 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packs.length === 0 ? (
              <div className="col-span-3 text-center text-gray-500">Žádné tarify nejsou k dispozici.</div>
            ) : (
              packs.map((pkg) => (
                <div
                  key={pkg.id}
                  className="border-2 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
                  style={{ borderColor: '#14A48D' }}
                >
                  <h2 className="text-2xl font-semibold mb-2">{pkg.credits} kreditů</h2>
                  <p className="text-gray-600 mb-2">{pkg.price_month} Kč / měsíc</p>
                  <p className="text-gray-500 text-sm">{pkg.name}</p>
                </div>
              ))
            )}
          </div>
        </div>
        {/* Box s obrázkem a textem pro změnu plánu */}
        <div className="container mx-auto mb-12 flex flex-col items-center max-w-xl">
          <div className="w-full bg-white border border-gray-300 rounded-lg shadow p-6 flex flex-col items-center">
            <h3 className="text-sm font-semibold text-gray-600 mb-2">Jak změnit plán?</h3>
            <img
              src="/images/change_plan.jpg"
              alt="Změna plánu"
              className="rounded-lg shadow mb-4 max-w-full h-auto"
              style={{ maxHeight: '300px' }}
            />
            <p className="text-center text-lg mb-4">
              V případě, že chcete změnit plán na vyšší nebo nižší, klikněte na tlačítko Aktualizovat předplatné.
            </p>
            <button
              onClick={handleManageSubscription}
              className="bg-[#14A48D] hover:bg-[#11997C] text-white font-bold py-3 px-6 rounded transition-colors text-lg"
            >
              Aktualizovat předplatné
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AccountPage; 